import { Modal } from 'antd'
import CantileverButton from 'components/CantileverButton'
import { MdCaseBadge } from 'features/mdCases/components/MdCaseBadge'
import type { MdCaseSeverity } from 'features/mdCases/mdCase'
import {
  formatMdCaseDateTimeRange,
  getSeverityColor,
} from 'features/mdCases/mdCaseDisplay'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import { X } from 'lucide-react'
import React from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'

import { HoverScrollableText } from './HoverScrollableText'
import type {
  CapturePhase,
  DraftAlertDisplay,
  DraftAlertFormState,
  DraftSelectionPreviewBounds,
  OverlayAlert,
  PendingCapture,
  PositionedOverlayAlert,
} from './mdDraftAlertCaseFlowTypes'
import { normalizeAlertDatapointIDs } from './mdDraftAlertCaseFlowUtils'

const ComposerField = styled.label`
  display: grid;
  gap: 0.35rem;

  > span {
    font-size: 0.8125rem;
    font-weight: 600;
    color: ${({ theme }) => theme.forecast.defaultText};
  }
`

const NativeSelect = styled.select`
  min-height: 2.25rem;
  padding: 0.45rem 0.6rem;
  border: 1px solid ${({ theme }) => theme.forecast.glassBackground};
  border-radius: 0.375rem;
  background: ${({ theme }) => theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.defaultText};
`

const ComposerTextInput = styled.input`
  min-height: 2.5rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid ${({ theme }) => theme.forecast.glassBackground};
  border-radius: 0.55rem;
  background: rgba(0, 0, 0, 0.34);
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.9375rem;

  &::placeholder {
    color: ${({ theme }) => theme.forecast.secondaryText};
  }
`

const ComposerGrid = styled.div`
  display: grid;
  gap: 0.875rem;
`

const ComposerNote = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const ComposerInfoRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`

const SeverityDot = styled.span<{ $color: string }>`
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 999px;
  background: ${({ $color }) => $color};
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.18);
  flex-shrink: 0;
`

const SensorChecklist = styled.div`
  display: grid;
  gap: 0.55rem;
  max-height: 12rem;
  overflow-y: auto;
  padding: 0.35rem;
  border: 1px solid ${({ theme }) => theme.forecast.glassBackground};
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.03);
`

const SensorOptionRow = styled.label<{ $checked: boolean }>`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 0.75rem;
  padding: 0.7rem 0.8rem;
  border: 1px solid
    ${({ theme, $checked }) =>
      $checked ? 'rgba(118, 193, 255, 0.55)' : theme.forecast.glassBackground};
  border-radius: 0.7rem;
  background: ${({ $checked }) =>
    $checked ? 'rgba(118, 193, 255, 0.12)' : 'rgba(255, 255, 255, 0.02)'};
  color: ${({ theme }) => theme.forecast.defaultText};
  cursor: pointer;

  input {
    width: 1rem;
    height: 1rem;
    margin: 0;
    accent-color: #76c1ff;
  }
`

const SensorOptionText = styled.div`
  display: grid;
  gap: 0.15rem;
  min-width: 0;

  > strong {
    font-size: 0.875rem;
    font-weight: 500;
    color: ${({ theme }) => theme.forecast.defaultText};
    overflow: hidden;
    text-overflow: ellipsis;
  }

  > small {
    font-size: 0.75rem;
    color: ${({ theme }) => theme.forecast.secondaryText};
  }
`

const CaptureOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 11;
  cursor: crosshair;
`

const CaptureBanner = styled.div`
  position: absolute;
  top: 0.75rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 12;
  padding: 0.45rem 0.75rem;
  border-radius: 999px;
  background: rgba(8, 15, 28, 0.9);
  border: 1px solid rgba(118, 193, 255, 0.35);
  color: #d9f0ff;
  font-size: 0.8125rem;
  pointer-events: none;
  width: min(32rem, calc(100% - 2rem));
  text-align: center;
  white-space: normal;
  line-height: 1.35;
`

const SelectionPreview = styled.div<{
  $left: number
  $top: number
  $width: number
  $height: number
  $borderColor: string
  $background: string
}>`
  position: absolute;
  left: ${({ $left }) => $left}px;
  top: ${({ $top }) => $top}px;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  border: 1px solid ${({ $borderColor }) => $borderColor};
  background: ${({ $background }) => $background};
  pointer-events: none;
`

const OverlayBand = styled.div<{
  $left: number
  $top: number
  $width: number
  $height: number
  $background: string
  $borderColor: string
  $dashed?: boolean
}>`
  position: absolute;
  left: ${({ $left }) => $left}px;
  top: ${({ $top }) => $top}px;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  background: ${({ $background }) => $background};
  border: 1px ${({ $dashed }) => ($dashed ? 'dashed' : 'solid')}
    ${({ $borderColor }) => $borderColor};
  pointer-events: none;
`

const OverlayBadge = styled.button<{
  $left: number
  $top: number
  $background: string
  $borderColor: string
  $isDraft?: boolean
}>`
  position: absolute;
  left: ${({ $left }) => $left}px;
  top: ${({ $top }) => $top}px;
  z-index: 8;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  max-width: 12rem;
  min-width: 0;
  padding: 0.25rem 0.45rem;
  border-radius: 999px;
  border: 1px ${({ $isDraft }) => ($isDraft ? 'dashed' : 'solid')}
    ${({ $borderColor }) => $borderColor};
  background: ${({ $background }) => $background};
  color: #fff;
  font-size: 0.75rem;
  line-height: 1.1;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.18);
  overflow: hidden;
  white-space: nowrap;

  &:hover {
    filter: brightness(1.05);
  }
`

const OverlayBadgeLabel = styled(HoverScrollableText)`
  flex: 1;
  min-width: 0;
`

const DetailCard = styled.div<{
  $left: number
  $top: number
}>`
  position: absolute;
  left: ${({ $left }) => `${$left}px`};
  top: ${({ $top }) => `${$top}px`};
  z-index: 12;
  width: min(14rem, calc(100vw - 2rem));
  display: grid;
  gap: 0.35rem;
  padding: 0.65rem 0.75rem;
  border-radius: 0.65rem;
  border: 1px solid ${({ theme }) => theme.forecast.glassBackground};
  background: rgba(9, 12, 20, 0.94);
  color: ${({ theme }) => theme.forecast.defaultText};
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.24);

  > strong {
    font-weight: 700;
    font-size: 0.875rem;
  }

  > small {
    font-size: 0.75rem;
    color: ${({ theme }) => theme.forecast.secondaryText};
  }

  > p {
    margin: 0;
    font-size: 0.8125rem;
    white-space: pre-wrap;
  }

  > div[data-actions='true'] {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }
`

const Tray = styled.div`
  position: absolute;
  right: 0.75rem;
  bottom: 0.75rem;
  z-index: 11;
  width: min(25rem, calc(100vw - 1.5rem));
  max-height: 50vh;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.forecast.glassBackground};
  border-radius: 0.75rem;
  background:
    linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.04) 0%,
      rgba(255, 255, 255, 0.01) 100%
    ),
    ${({ theme }) => theme.forecast.pageBackground1};
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.22);
  backdrop-filter: blur(10px);
`

const TrayHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 0.875rem;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.glassBackground};
`

const TrayTitle = styled.div`
  display: grid;
  gap: 0.15rem;

  > strong {
    font-weight: 700;
    color: ${({ theme }) => theme.forecast.defaultText};
  }

  > small {
    font-size: 0.8125rem;
    color: ${({ theme }) => theme.forecast.secondaryText};
  }
`

const TrayList = styled.div`
  display: grid;
  gap: 0.75rem;
  padding: 0.875rem;
  max-height: 18rem;
  overflow-y: auto;
`

const TrayItem = styled.div`
  display: grid;
  gap: 0.35rem;
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.forecast.glassBackground};
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.forecast.pageBackground1};
`

const TrayItemTop = styled.div`
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: 0.75rem;
`

const TrayItemTitle = styled.div`
  display: grid;
  gap: 0.1rem;
  min-width: 0;

  > small {
    font-size: 0.8125rem;
    color: ${({ theme }) => theme.forecast.secondaryText};
  }
`

const TrayItemAlertTitle = styled.strong`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 0;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const TrayItemAlertName = styled(HoverScrollableText)`
  flex: 1;
  min-width: 0;
`

const TrayItemSummary = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  white-space: pre-wrap;
`

const TrayFooter = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 0.875rem;
  border-top: 1px solid ${({ theme }) => theme.forecast.glassBackground};
`

const ReopenTrayButton = styled(CantileverButton)`
  position: absolute;
  right: 0.75rem;
  bottom: 0.75rem;
  z-index: 11;
  background: rgba(16, 22, 34, 0.94) !important;
  border: 1px solid ${({ theme }) => theme.forecast.glassBackground} !important;
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.24);
  color: ${({ theme }) => theme.forecast.defaultText} !important;
`

const MutedMeta = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  max-width: 12rem;
`

type SensorLike = {
  id: string
  data: {
    name?: string | null
  }
}

export interface MdDraftAlertCaseFlowPanelsProps {
  chartAreaRef: React.MutableRefObject<HTMLDivElement | null>
  visibleSensors: readonly SensorLike[]
  formState: DraftAlertFormState
  setFormState: React.Dispatch<React.SetStateAction<DraftAlertFormState>>
  pendingCapture: PendingCapture | null
  isComposerOpen: boolean
  onCancelComposer: () => void
  onSubmitComposer: () => void
  positionedAlerts: readonly PositionedOverlayAlert[]
  activePositionedAlert: PositionedOverlayAlert | null
  capturePhase: CapturePhase
  draftSelectionPreview: DraftSelectionPreviewBounds | null
  sensorsById: Map<string, SensorLike>
  isDeletingAlertId: string | null
  allowSavedAlertDelete: boolean
  onDeleteAlert: (alert: OverlayAlert) => Promise<void>
  onOpenCase: (caseId: string | null) => void
  onStartHoverAlert: (alertId: string) => void
  onScheduleHoverAlertHide: (alertId: string) => void
  onToggleAlert: (alertId: string) => void
  dismissedAlertIds: ReadonlySet<string>
  captureHandlers: {
    onMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void
    onMouseMove: (event: React.MouseEvent<HTMLDivElement>) => void
    onMouseUp: (event?: React.MouseEvent<HTMLDivElement>) => void
  }
  draftAlerts: readonly DraftAlertDisplay[]
  isTrayVisible: boolean
  setIsTrayVisible: React.Dispatch<React.SetStateAction<boolean>>
  isCreatingCase: boolean
  onCreateCase: () => Promise<void>
  onRemoveDraftAlert: (alertId: string) => void
}

export const MdDraftAlertCaseFlowPanels: React.FC<
  MdDraftAlertCaseFlowPanelsProps
> = ({
  chartAreaRef,
  visibleSensors,
  formState,
  setFormState,
  pendingCapture,
  isComposerOpen,
  onCancelComposer,
  onSubmitComposer,
  positionedAlerts,
  activePositionedAlert,
  capturePhase,
  draftSelectionPreview,
  sensorsById,
  isDeletingAlertId,
  allowSavedAlertDelete,
  onDeleteAlert,
  onOpenCase,
  onStartHoverAlert,
  onScheduleHoverAlertHide,
  onToggleAlert,
  dismissedAlertIds,
  captureHandlers,
  draftAlerts,
  isTrayVisible,
  setIsTrayVisible,
  isCreatingCase,
  onCreateCase,
  onRemoveDraftAlert,
}) => {
  const getSensorName = (sensorId: string) =>
    sensorsById.get(sensorId)?.data.name || sensorId
  const relatedVisibleSensors = visibleSensors.filter(
    (sensor) => sensor.id !== formState.primarySensorId
  )
  const draftAlertCountLabel = `${draftAlerts.length} alert${
    draftAlerts.length === 1 ? '' : 's'
  }`
  const isComposerSubmitDisabled =
    !pendingCapture ||
    !formState.primarySensorId ||
    !formState.title.trim() ||
    !formState.sensorIds.includes(formState.primarySensorId)

  const overlayHost = chartAreaRef.current
  const shouldPortal = overlayHost?.isConnected ?? false

  const overlayContent = (
    <>
      {positionedAlerts.map((alert) => {
        const color = getSeverityColor(alert.severity)
        const badgeLabel = alert.title || getSensorName(alert.primarySensorId)
        const isDismissed = dismissedAlertIds.has(alert.id)

        return (
          <React.Fragment key={alert.id}>
            {!isDismissed && (
              <OverlayBand
                $left={alert.focusLeft}
                $top={alert.plotTop}
                $width={alert.focusWidth}
                $height={alert.plotHeight}
                $background={`${color}38`}
                $borderColor={color}
                $dashed={alert.kind === 'draft'}
              />
            )}
            <OverlayBadge
              type='button'
              $left={alert.badgeLeft}
              $top={alert.badgeTop}
              $background={alert.kind === 'draft' ? `${color}3a` : `${color}cc`}
              $borderColor={color}
              $isDraft={alert.kind === 'draft'}
              style={isDismissed ? { opacity: 0.45 } : undefined}
              title={
                isDismissed ? 'Click to restore alert highlight' : undefined
              }
              onMouseEnter={
                isDismissed ? undefined : () => onStartHoverAlert(alert.id)
              }
              onMouseLeave={
                isDismissed
                  ? undefined
                  : () => onScheduleHoverAlertHide(alert.id)
              }
              onClick={() => onToggleAlert(alert.id)}>
              {alert.severity && <SeverityDot $color={color} />}
              {alert.kind === 'draft' ? 'Draft' : 'Alert'}
              {' · '}
              <OverlayBadgeLabel text={badgeLabel} />
            </OverlayBadge>
          </React.Fragment>
        )
      })}

      {capturePhase !== 'idle' && (
        <CaptureOverlay
          data-testid='manual-alert-capture-overlay'
          onMouseDown={captureHandlers.onMouseDown}
          onMouseMove={captureHandlers.onMouseMove}
          onMouseUp={captureHandlers.onMouseUp}
          onMouseLeave={captureHandlers.onMouseUp}>
          <CaptureBanner>
            Drag across the area you want highlighted for this alert.
          </CaptureBanner>

          {draftSelectionPreview && (
            <SelectionPreview
              $left={draftSelectionPreview.left}
              $top={draftSelectionPreview.top}
              $width={draftSelectionPreview.width}
              $height={draftSelectionPreview.height}
              $borderColor='rgba(255, 216, 122, 0.95)'
              $background='rgba(255, 216, 122, 0.22)'
            />
          )}
        </CaptureOverlay>
      )}

      {activePositionedAlert && (
        <DetailCard
          $left={activePositionedAlert.detailLeft}
          $top={activePositionedAlert.detailTop}
          data-testid='manual-alert-detail-card'
          onMouseEnter={() => onStartHoverAlert(activePositionedAlert.id)}
          onMouseLeave={() =>
            onScheduleHoverAlertHide(activePositionedAlert.id)
          }>
          <strong>
            {activePositionedAlert.title ||
              getSensorName(activePositionedAlert.primarySensorId)}
          </strong>
          <small>
            {activePositionedAlert.kind === 'draft'
              ? 'Draft alert'
              : 'Saved alert'}
            {' · '}
            {activePositionedAlert.severity ?? 'Severity not set'}
          </small>
          <small>
            Range:{' '}
            {formatMdCaseDateTimeRange(
              activePositionedAlert.focusStartAt,
              activePositionedAlert.focusEndAt
            )}
          </small>
          {activePositionedAlert.summary &&
            activePositionedAlert.summary !== activePositionedAlert.title && (
              <p>{activePositionedAlert.summary}</p>
            )}
          {activePositionedAlert.kind === 'saved' &&
            (allowSavedAlertDelete || activePositionedAlert.caseId) && (
              <div data-actions='true'>
                {allowSavedAlertDelete && (
                  <CantileverButton
                    ghost
                    danger
                    onClick={() => void onDeleteAlert(activePositionedAlert)}
                    disabled={isDeletingAlertId === activePositionedAlert.id}>
                    {isDeletingAlertId === activePositionedAlert.id
                      ? 'Deleting...'
                      : 'Delete'}
                  </CantileverButton>
                )}
                {activePositionedAlert.caseId && (
                  <CantileverButton
                    ghost
                    onClick={() => onOpenCase(activePositionedAlert.caseId)}>
                    Open Case
                  </CantileverButton>
                )}
              </div>
            )}
        </DetailCard>
      )}

      {draftAlerts.length > 0 &&
        (isTrayVisible ? (
          <Tray>
            <TrayHeader>
              <TrayTitle>
                <strong>Draft Alerts</strong>
                <small>{draftAlertCountLabel} ready to turn into a case</small>
              </TrayTitle>
              <ChartTooltip label='Dismiss draft alerts tray' side='top'>
                <CantileverButton
                  iconOnly
                  ghost
                  size='small'
                  aria-label='Dismiss draft alerts tray'
                  onClick={() => setIsTrayVisible(false)}>
                  <X size={14} />
                </CantileverButton>
              </ChartTooltip>
            </TrayHeader>

            <TrayList>
              {draftAlerts.map((alert) => {
                const primarySensorName = getSensorName(alert.primarySensorId)

                return (
                  <TrayItem key={alert.id}>
                    <TrayItemTop>
                      <TrayItemTitle>
                        <TrayItemAlertTitle>
                          {alert.severity && (
                            <SeverityDot
                              $color={getSeverityColor(alert.severity || null)}
                            />
                          )}
                          <TrayItemAlertName
                            text={alert.title || primarySensorName}
                          />
                        </TrayItemAlertTitle>
                        <small>
                          {primarySensorName}
                          {alert.sensorIds.length > 1
                            ? ` +${alert.sensorIds.length - 1} stream${alert.sensorIds.length === 2 ? '' : 's'}`
                            : ''}
                        </small>
                        <ComposerInfoRow>
                          <MdCaseBadge $tone='default'>
                            Range:{' '}
                            {formatMdCaseDateTimeRange(
                              alert.focusStartAt,
                              alert.focusEndAt
                            )}
                          </MdCaseBadge>
                        </ComposerInfoRow>
                      </TrayItemTitle>
                      <ChartTooltip label='Remove drafted alert' side='left'>
                        <CantileverButton
                          iconOnly
                          ghost
                          size='small'
                          aria-label='Remove drafted alert'
                          onClick={() => onRemoveDraftAlert(alert.id)}>
                          <X size={14} />
                        </CantileverButton>
                      </ChartTooltip>
                    </TrayItemTop>
                    {alert.summary && alert.summary !== alert.title && (
                      <TrayItemSummary>{alert.summary}</TrayItemSummary>
                    )}
                  </TrayItem>
                )
              })}
            </TrayList>

            <TrayFooter>
              <MutedMeta>
                Drafts stay local to this page session until you create the
                case.
              </MutedMeta>
              <CantileverButton
                primary
                onClick={() => void onCreateCase()}
                disabled={isCreatingCase}>
                {isCreatingCase
                  ? 'Creating Case...'
                  : 'Make Case From These Alerts'}
              </CantileverButton>
            </TrayFooter>
          </Tray>
        ) : (
          <ReopenTrayButton ghost onClick={() => setIsTrayVisible(true)}>
            Draft Alerts ({draftAlertCountLabel})
          </ReopenTrayButton>
        ))}
    </>
  )

  return (
    <>
      <Modal
        title='Draft Manual Alert'
        open={isComposerOpen}
        onCancel={onCancelComposer}
        onOk={onSubmitComposer}
        okText='Add To Tray'
        width={520}
        zIndex={2000}
        style={{ top: 72 }}
        okButtonProps={{
          disabled: isComposerSubmitDisabled,
          style: {
            background: '#424a57',
            borderColor: '#596273',
            color: '#eef3f8',
          },
        }}>
        <ComposerGrid>
          <ComposerField>
            <span>Primary Stream</span>
            <NativeSelect
              value={formState.primarySensorId}
              onChange={(event) => {
                const nextPrimarySensorId = event.target.value
                setFormState((currentState) => ({
                  ...currentState,
                  primarySensorId: nextPrimarySensorId,
                  sensorIds: normalizeAlertDatapointIDs(
                    nextPrimarySensorId,
                    currentState.sensorIds
                  ),
                }))
              }}>
              {visibleSensors.map((sensor) => (
                <option key={sensor.id} value={sensor.id}>
                  {sensor.data.name || sensor.id}
                </option>
              ))}
            </NativeSelect>
          </ComposerField>

          <ComposerField>
            <span>Related Visible Streams</span>
            <ComposerNote>
              Add any nearby streams that should stay associated with this
              alert.
            </ComposerNote>
            <SensorChecklist>
              {relatedVisibleSensors.map((sensor) => {
                const checked = formState.sensorIds.includes(sensor.id)

                return (
                  <SensorOptionRow key={sensor.id} $checked={checked}>
                    <input
                      type='checkbox'
                      checked={checked}
                      onChange={(event) =>
                        setFormState((currentState) => ({
                          ...currentState,
                          sensorIds: event.target.checked
                            ? normalizeAlertDatapointIDs(
                                currentState.primarySensorId,
                                [...currentState.sensorIds, sensor.id]
                              )
                            : currentState.sensorIds.filter(
                                (sensorId) => sensorId !== sensor.id
                              ),
                        }))
                      }
                    />
                    <SensorOptionText>
                      <strong>{sensor.data.name || sensor.id}</strong>
                      <small>{sensor.id}</small>
                    </SensorOptionText>
                  </SensorOptionRow>
                )
              })}
              {relatedVisibleSensors.length === 0 && (
                <ComposerNote>
                  No additional visible streams are available for this alert.
                </ComposerNote>
              )}
            </SensorChecklist>
          </ComposerField>

          <ComposerField>
            <span>Alert Title</span>
            <ComposerTextInput
              value={formState.title}
              onChange={(event) =>
                setFormState((currentState) => ({
                  ...currentState,
                  title: event.target.value,
                }))
              }
              placeholder='Required short alert title'
            />
          </ComposerField>

          <ComposerField>
            <span>Severity</span>
            <NativeSelect
              value={formState.severity}
              onChange={(event) =>
                setFormState((currentState) => ({
                  ...currentState,
                  severity: event.target.value as '' | MdCaseSeverity,
                }))
              }>
              <option value=''>Not set</option>
              {['High', 'Medium', 'Low'].map((severity) => (
                <option key={severity} value={severity}>
                  {severity}
                </option>
              ))}
            </NativeSelect>
          </ComposerField>

          {pendingCapture && (
            <ComposerInfoRow>
              <MdCaseBadge $tone='default'>
                Alert Range:{' '}
                {formatMdCaseDateTimeRange(
                  pendingCapture.focusStartAt,
                  pendingCapture.focusEndAt
                )}
              </MdCaseBadge>
            </ComposerInfoRow>
          )}
        </ComposerGrid>
      </Modal>

      {shouldPortal && overlayHost
        ? createPortal(overlayContent, overlayHost)
        : overlayContent}
    </>
  )
}
